import {
    BaseButton,
    claimActivity,
    claimDiagnosisCodes,
    claimPatientName,
    denialAgeDays,
    formatCurrency,
    formatDate,
    IDenialRemarkRow,
    ProviderClaim,
    renderingNpi,
    SidePanel,
} from '@enter/core';
import {
    mdiChevronRight,
    mdiClipboardPlusOutline,
    mdiClose,
    mdiMinusCircleOutline,
    mdiOpenInNew,
    mdiPlaylistEdit,
    mdiRobotOutline,
    mdiWalletOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DenialsStore } from 'stores/denials.store';
import * as S from './claim-drawer.styles';

/**
 * One claim behind a denial, in a drawer (FE-116, design 05) — the denial variant only.
 *
 * Opened from a payment row in the drill-down. What the design shows and this does not, and why:
 *
 * - **Balance** — `ProviderClaim` has no balance field. Shown pending, with the reason (NS-4).
 * - **Deadline** — nothing in the API states a payer's timely-filing limit. Pending (decided
 *   2026-10-01).
 * - **The plain-English explanation and ENTER RECOMMENDS** — omitted (decided 2026-10-01): there is no
 *   source for either, and a written-up recommendation on a live claim is advice we cannot stand behind.
 * - **Convert to Private Pay** — the self-pay endpoint is Support-only; the provider API has none.
 *
 * Every action here is an EXISTING flow, reached from a new place — Close Out is the claim page's own
 * dialog, Create a Task is the claim banner's own compose, Edit Claim is the editor route. Nothing in
 * this file writes to a claim itself.
 */

const PENDING = {
    balance: 'Not available yet: the claim record carries no balance field.',
    deadline: 'Not available yet: no source states this payer’s timely-filing limit.',
    selfPay: 'Not available here: converting a claim to private pay is a Support action, with no provider endpoint.',
    codes: 'Diagnosis codes and NPI are read from the professional claim form; this claim has none.',
};

export interface IClaimDrawerProps {
    store: DenialsStore;
    /** The drill-down row the payment was opened from — the denial being shown. */
    row: IDenialRemarkRow;
    accountId?: string;
    onClose: () => void;
    onConfigureWorker: () => void;
    onCloseOut: (claim: ProviderClaim) => void;
    onCreateTask: (claim: ProviderClaim) => void;
    onEditClaim: (claimId: string) => void;
}

const money = (amount: { amount?: string } | undefined): string =>
    amount?.amount !== undefined ? formatCurrency(Number(amount.amount).toFixed(2)) : '–';

const ago = (date: string | undefined): string | undefined => {
    if (!date) {
        return undefined;
    }
    try {
        return formatDistanceToNowStrict(parseISO(date), { addSuffix: true });
    } catch {
        return undefined;
    }
};

export const ClaimDrawer: React.FC<IClaimDrawerProps> = observer(
    ({ store, row, accountId, onClose, onConfigureWorker, onCloseOut, onCreateTask, onEditClaim }) => {
        const payment = store.drawerPayment;
        const bodyRef = React.useRef<HTMLDivElement>(null);
        const onCloseRef = React.useRef(onClose);
        onCloseRef.current = onClose;

        // Keyboard: focus moves into the drawer when it opens, Escape closes it, and focus goes back to
        // where it was. Not a focus TRAP — the drawer is non-modal (aria-modal="false"); the page behind
        // it stays usable. Escape is left to a dialog opened from the drawer while one is on top.
        const openId = payment?.id;
        React.useEffect(() => {
            if (!openId) {
                return undefined;
            }
            const previous = document.activeElement as HTMLElement | null;
            bodyRef.current?.querySelector<HTMLElement>('button[aria-label="Close claim"]')?.focus();
            const onKey = (event: KeyboardEvent): void => {
                if (event.key === 'Escape' && !document.querySelector('.MuiDialog-root')) {
                    onCloseRef.current();
                }
            };
            document.addEventListener('keydown', onKey);
            return (): void => {
                document.removeEventListener('keydown', onKey);
                previous?.focus?.();
            };
        }, [openId]);
        const claim = store.drawerClaim;
        if (!payment) {
            return null;
        }

        const claimHref = claim?.id ? (accountId ? `/${accountId}/claims/${claim.id}` : `/claims/${claim.id}`) : undefined;
        const age = denialAgeDays(payment, new Date());
        const details = store.drawerDetails;
        const diagnoses = claimDiagnosisCodes(details);
        const npi = renderingNpi(details);
        const activity = claimActivity(store.drawerHistory);
        const subtitle = [
            claim?.id && `Claim ${claim.id}`,
            claim?.fromDos && `DOS ${formatDate(claim.fromDos, 'MMM d yyyy')}`,
            claim?.primaryPayerName,
        ]
            .filter(Boolean)
            .join(' · ');

        return (
            <SidePanel className="fixed below-topnav">
                <S.Body ref={bodyRef} role="dialog" aria-modal="false" aria-label="Claim">
                    <S.Header>
                        <S.TitleRow>
                            <S.TitleText>
                                <S.Title>
                                    {claim === undefined && !store.drawerError
                                        ? 'Loading claim…'
                                        : claim
                                          ? claimPatientName(claim)
                                          : 'Claim not found'}
                                </S.Title>
                                {subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
                            </S.TitleText>
                            {claimHref && (
                                <BaseButton
                                    variant="text"
                                    size="small"
                                    aria-label="Open claim in a new tab"
                                    onClick={(): void => {
                                        window.open(claimHref, '_blank', 'noopener');
                                    }}
                                >
                                    <Icon path={mdiOpenInNew} size={0.8} />
                                </BaseButton>
                            )}
                            <BaseButton variant="text" size="small" aria-label="Close claim" onClick={onClose}>
                                <Icon path={mdiClose} size={0.8} />
                            </BaseButton>
                        </S.TitleRow>
                        <S.Kpis>
                            <S.Kpi>
                                <S.KpiLabel>Charge</S.KpiLabel>
                                <S.KpiValue>{claim ? money(claim.chargeAmount) : '–'}</S.KpiValue>
                            </S.Kpi>
                            <S.Kpi>
                                <S.KpiLabel>Balance</S.KpiLabel>
                                <S.KpiValue pending title={PENDING.balance}>
                                    &mdash;
                                </S.KpiValue>
                            </S.Kpi>
                            <S.Kpi>
                                <S.KpiLabel>Age</S.KpiLabel>
                                <S.KpiValue title="Days since the remittance carrying this denial was received">
                                    {age === undefined ? '–' : `${age}d`}
                                </S.KpiValue>
                            </S.Kpi>
                            <S.Kpi>
                                <S.KpiLabel>Deadline</S.KpiLabel>
                                <S.KpiValue pending title={PENDING.deadline}>
                                    &mdash;
                                </S.KpiValue>
                            </S.Kpi>
                        </S.Kpis>
                    </S.Header>

                    <S.Scroll>
                        {store.drawerError ? (
                            <S.Block>
                                <S.Muted>{store.drawerError}</S.Muted>
                            </S.Block>
                        ) : claim === null ? (
                            <S.Block>
                                <S.Muted>
                                    This payment does not resolve to a claim. The denial is real; the claim it settles
                                    could not be found from it.
                                </S.Muted>
                            </S.Block>
                        ) : null}

                        <S.Block aria-label="Denial">
                            <S.Badge>DENIAL</S.Badge>
                            <S.Reason>
                                <strong>{row.codes}</strong> &mdash; {row.reasonDescription}
                            </S.Reason>
                            {row.remarkCodes.length > 0 && (
                                <S.Remark>
                                    <strong>{row.remarkLabel}</strong> &mdash; {row.remarkDescription}
                                </S.Remark>
                            )}
                            <S.Chips>
                                {payment.procedureCodes.map(code => (
                                    <S.Chip key={`cpt-${code}`} title="Procedure code on the denied line">
                                        {code}
                                    </S.Chip>
                                ))}
                                {claim && !details && !store.drawerDetailsError && (
                                    <S.Chip pending>Loading codes&hellip;</S.Chip>
                                )}
                                {diagnoses.map(code => (
                                    <S.Chip key={`icd-${code}`} title="Diagnosis code (box 21)">
                                        {code}
                                    </S.Chip>
                                ))}
                                {npi && <S.Chip title="Rendering provider NPI">NPI {npi}</S.Chip>}
                                {details && !diagnoses.length && !npi && (
                                    <S.Chip pending title={PENDING.codes}>
                                        No diagnosis codes or NPI
                                    </S.Chip>
                                )}
                                {store.drawerDetailsError && <S.Chip pending>{store.drawerDetailsError}</S.Chip>}
                            </S.Chips>
                        </S.Block>

                        <S.Block>
                            <S.WorkerCard variant="unstyled" type="button" onClick={onConfigureWorker}>
                                <Icon path={mdiRobotOutline} size={1} />
                                <S.WorkerText>
                                    <S.WorkerTitle>Configure Denial Worker</S.WorkerTitle>
                                    <S.WorkerMeta>
                                        Remits carrying {row.codes} &mdash; this one and future ones &mdash; are worked
                                        automatically
                                    </S.WorkerMeta>
                                </S.WorkerText>
                                <Icon path={mdiChevronRight} size={0.9} />
                            </S.WorkerCard>

                            <S.Eyebrow>Or work this claim once</S.Eyebrow>
                            <S.Actions>
                                <BaseButton variant="outlined" disabled={!claim} onClick={(): void => claim && onCloseOut(claim)}>
                                    <S.ActionLabel>
                                        <Icon path={mdiMinusCircleOutline} size={0.8} />
                                        Close Out
                                    </S.ActionLabel>
                                </BaseButton>
                                <BaseButton variant="outlined" disabled={!claim} onClick={(): void => claim && onCreateTask(claim)}>
                                    <S.ActionLabel>
                                        <Icon path={mdiClipboardPlusOutline} size={0.8} />
                                        Create a Task
                                    </S.ActionLabel>
                                </BaseButton>
                                <BaseButton
                                    variant="outlined"
                                    disabled={!claim?.id}
                                    onClick={(): void => {
                                        if (claim?.id) {
                                            onEditClaim(claim.id);
                                        }
                                    }}
                                >
                                    <S.ActionLabel>
                                        <Icon path={mdiPlaylistEdit} size={0.8} />
                                        Edit Claim
                                    </S.ActionLabel>
                                </BaseButton>
                                <S.ActionSlot title={PENDING.selfPay}>
                                    <BaseButton variant="outlined" disabled>
                                        <S.ActionLabel>
                                            <Icon path={mdiWalletOutline} size={0.8} />
                                            Convert to Private Pay
                                        </S.ActionLabel>
                                    </BaseButton>
                                </S.ActionSlot>
                            </S.Actions>
                        </S.Block>

                        <S.Block aria-label="Activity">
                            <S.Eyebrow>Activity</S.Eyebrow>
                            {store.drawerHistoryError ? (
                                <S.Muted>{store.drawerHistoryError}</S.Muted>
                            ) : !store.drawerHistory ? (
                                <S.Muted>{claim === null ? 'No claim, so no activity.' : 'Loading activity…'}</S.Muted>
                            ) : !activity.length ? (
                                <S.Muted>The worker has recorded nothing on this claim.</S.Muted>
                            ) : (
                                <S.Activity>
                                    {activity.map((item, index) => (
                                        <S.ActivityItem key={item.key}>
                                            <S.Dot first={index === 0} />
                                            <S.ActivityText>
                                                <span>
                                                    {item.title}
                                                    {item.step ? ` · ${item.step}` : ''}
                                                </span>
                                                <S.ActivityMeta>
                                                    {[ago(item.date), item.status].filter(Boolean).join(' · ')}
                                                </S.ActivityMeta>
                                            </S.ActivityText>
                                        </S.ActivityItem>
                                    ))}
                                </S.Activity>
                            )}
                        </S.Block>
                    </S.Scroll>
                </S.Body>
            </SidePanel>
        );
    },
);
